import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';

interface BarChartProps {
  /** Chart data */
  data: any[];
  /** Chart title */
  title?: string;
  /** Chart height in pixels */
  height?: number;
  /** Custom className */
  className?: string;
  /** X-axis data key */
  xAxisKey?: string;
  /** Bar data key */
  dataKey?: string;
  /** Bar color */
  barColor?: string;
}

/**
 * BarChart wrapper component for categorical data
 *
 * @example
 * ```tsx
 * <BarChart
 *   data={topCountries}
 *   title="Top Countries"
 *   xAxisKey="country"
 *   dataKey="count"
 *   barColor="#3b82f6"
 * />
 * ```
 */
export const BarChart: React.FC<BarChartProps> = ({
  data,
  title,
  height = 300,
  className,
  xAxisKey = 'name',
  dataKey = 'count',
  barColor = '#3b82f6',
}) => {
  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200',
          className
        )}
        style={{ height }}
      >
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey={dataKey} fill={barColor} />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChart;
